<script setup>
import { computed } from 'vue'

import { AppIcon } from '@/components/shared'

/**
 * Standalone eyebrow - the small mono-caps strip used as a label above a
 * section, callout, or identity chip. Use this when there isn't a `<Heading>`
 * directly underneath (in which case prefer `<Heading eyebrow="..." />`, which
 * pairs the eyebrow with the headline in one component).
 *
 * Locks down the visual ladder we hand-rolled across the app:
 *   - `font-mono` + `tracking-widest` (mono-caps eyebrow style)
 *   - `uppercase`
 *   - Size in the 10/11/12 ladder
 *
 * Two color variants - the inconsistency the audit flagged was tracking
 * drift, NOT color, so we keep both intentional uses:
 *   - `primary` (default) → orange `text-primary-container`. Used for callout
 *     headers, identity strips, section markers (`§ 1` legal pages, "TIP PRO
 *     FOTOGRAFY" Studio dashboard).
 *   - `muted` → `text-zinc-400`. Used for quiet section labels above
 *     supplementary content ("Související dokumenty", footer asides).
 *
 * Optional leading icon (Material Symbol) - inherits the eyebrow color.
 * Decorative; never read by screen readers.
 */
const props = defineProps({
    /** Visible label text. */
    text: {
        type: String,
        required: true,
    },
    /**
     * Optional Material Symbols glyph rendered inline before the text.
     */
    icon: {
        type: String,
        default: '',
    },
    /**
     * `xs` 10px (callout chips inside cards), `sm` 11px (page eyebrows above
     * h1, default), `md` 12px (rare - hero page eyebrows where the heading is
     * larger).
     */
    size: {
        type: String,
        default: 'sm',
        validator: (v) => ['xs', 'sm', 'md'].includes(v),
    },
    /**
     * `primary` (default) - orange callout eyebrow.
     * `muted` - zinc-400 supplementary label.
     */
    variant: {
        type: String,
        default: 'primary',
        validator: (v) => ['primary', 'muted'].includes(v),
    },
})

const SIZE_CLASSES = {
    xs: 'text-[10px]',
    sm: 'text-[11px]',
    md: 'text-xs',
}

const colorClass = computed(() => (props.variant === 'muted' ? 'text-zinc-400' : 'text-primary-container'))
</script>

<template>
    <p
        class="font-mono font-bold tracking-widest uppercase"
        :class="[SIZE_CLASSES[size], colorClass, icon ? 'inline-flex items-center gap-2' : '']"
    >
        <AppIcon
            class="text-sm"
            v-if="icon"
            :name="icon"
        />
        {{ text }}
    </p>
</template>
